import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePriorities, PRIORITY_TONE } from "@/context/PrioritiesContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, ChevronUp, ChevronDown, Clock, Layers, Building2, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = Object.keys(PRIORITY_TONE);
const fmtMins = (m) => {
  if (!m) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};
const errMsg = (e, fb) => e?.response?.data?.detail || fb;

/* ============================= Priorities ============================= */
function PriorityDialog({ open, onOpenChange, target, onSaved }) {
  const blank = { name: "", description: "", color: "blue", first_response_minutes: 60, resolution_minutes: 240, active: true };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(target ? { ...blank, ...target } : blank); /* eslint-disable-next-line */ }, [target, open]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name, description: form.description, color: form.color,
        first_response_minutes: Number(form.first_response_minutes),
        resolution_minutes: Number(form.resolution_minutes), active: form.active,
      };
      if (target) await api.patch(`/sla-priorities/${target.id}`, payload);
      else await api.post("/sla-priorities", payload);
      toast.success(`Priority ${target ? "updated" : "created"}`);
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e, "Save failed")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sla-priority-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{target ? "Edit" : "New"} SLA Priority</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Priority name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Urgent" data-testid="sla-priority-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>First response (min)</Label>
              <Input type="number" min="1" value={form.first_response_minutes}
                onChange={(e) => setForm({ ...form, first_response_minutes: e.target.value })}
                data-testid="sla-priority-fr" />
              <p className="text-[10px] text-slate-400">{fmtMins(Number(form.first_response_minutes))}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Resolution (min)</Label>
              <Input type="number" min="1" value={form.resolution_minutes}
                onChange={(e) => setForm({ ...form, resolution_minutes: e.target.value })}
                data-testid="sla-priority-res" />
              <p className="text-[10px] text-slate-400">{fmtMins(Number(form.resolution_minutes))}</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-1.5">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                  data-testid={`sla-color-${c}`}
                  className={`px-2 py-0.5 rounded-full text-[11px] border ${PRIORITY_TONE[c]} ${form.color === c ? "ring-2 ring-offset-1 ring-indigo-400" : ""}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sla-priority-desc" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="sla-priority-active" />
            <Label className="cursor-pointer">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={busy || !form.name.trim()} data-testid="sla-priority-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrioritiesSection({ canWrite }) {
  const { refresh: refreshCtx, toneClass } = usePriorities();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, target: null });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/sla-priorities").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const onSaved = () => { load(); refreshCtx(); };

  const move = async (idx, dir) => {
    const next = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
    await api.post("/sla-priorities/reorder", { ordered_ids: next.map((r) => r.id) });
    refreshCtx();
  };

  const del = async (r) => {
    if (!window.confirm(`Delete priority "${r.name}"?`)) return;
    try { await api.delete(`/sla-priorities/${r.id}`); toast.success("Deleted"); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/sla-priorities/${r.id}`, { ...r, active: !r.active });
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Update failed")); }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-base flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-500" /> SLA Priorities</h3>
            <p className="text-xs text-slate-500 mt-0.5">The single source of truth for SLA durations. Taxonomy & tickets reference these live.</p>
          </div>
          {canWrite && (
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setDlg({ open: true, target: null })} data-testid="add-sla-priority-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Priority
            </Button>
          )}
        </div>
        {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div> : (
          <div className="overflow-x-auto" data-testid="sla-priorities-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 w-20">Order</th>
                  <th className="text-left py-2">Priority</th>
                  <th className="text-left py-2">First response</th>
                  <th className="text-left py-2">Resolution</th>
                  <th className="text-left py-2">Status</th>
                  {canWrite && <th className="text-right py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`sla-priority-row-${r.key}`}>
                    <td className="py-2">
                      {canWrite && (
                        <div className="flex gap-0.5">
                          <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`sla-up-${r.key}`}><ChevronUp className="w-4 h-4" /></button>
                          <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`sla-down-${r.key}`}><ChevronDown className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                    <td className="py-2">
                      <Badge variant="outline" className={`rounded-full ${toneClass(r.color)}`}>{r.name}</Badge>
                      {r.description ? <span className="text-[11px] text-slate-400 ml-2">{r.description}</span> : null}
                    </td>
                    <td className="py-2 text-slate-700">{fmtMins(r.first_response_minutes)}</td>
                    <td className="py-2 text-slate-700">{fmtMins(r.resolution_minutes)}</td>
                    <td className="py-2">
                      {canWrite
                        ? <Switch checked={r.active !== false} onCheckedChange={() => toggleActive(r)} data-testid={`sla-toggle-${r.key}`} />
                        : <Badge variant="outline" className={r.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}>{r.active !== false ? "Active" : "Inactive"}</Badge>}
                    </td>
                    {canWrite && (
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDlg({ open: true, target: r })} data-testid={`edit-sla-${r.key}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => del(r)} data-testid={`delete-sla-${r.key}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PriorityDialog open={dlg.open} onOpenChange={(v) => setDlg({ ...dlg, open: v })} target={dlg.target} onSaved={onSaved} />
      </CardContent>
    </Card>
  );
}

/* ============================= Clients ============================= */
function ClientsSection({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", code: "" });
  const load = useCallback(() => { api.get("/customer-clients").then((r) => setRows(r.data || [])); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return;
    try { await api.post("/customer-clients", form); toast.success("Client added"); setForm({ name: "", code: "" }); load(); }
    catch (e) { toast.error(errMsg(e, "Add failed")); }
  };
  const del = async (r) => {
    try { await api.delete(`/customer-clients/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <h3 className="font-heading text-base flex items-center gap-2 mb-1"><Building2 className="w-4 h-4 text-indigo-500" /> Clients / Accounts</h3>
        <p className="text-xs text-slate-500 mb-4">Group customers under a client to apply client-specific SLA overrides.</p>
        {canWrite && (
          <div className="flex gap-2 mb-3">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client name" className="h-9" data-testid="client-name-input" />
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Code (opt)" className="h-9 w-32" data-testid="client-code-input" />
            <Button size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700" onClick={add} data-testid="add-client-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
        <ul className="space-y-1.5" data-testid="clients-list">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2">
              <span className="text-sm text-slate-800">{r.name} {r.code ? <span className="text-[11px] text-slate-400 font-mono ml-1">{r.code}</span> : null}</span>
              {canWrite && <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => del(r)} data-testid={`delete-client-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </li>
          ))}
          {rows.length === 0 && <li className="text-sm text-slate-400 py-2">No clients yet.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}

/* ============================= Overrides ============================= */
function OverridesSection({ canWrite }) {
  const { priorities } = usePriorities();
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ priority_key: "", scope_type: "department", scope_id: "", first_response_minutes: 30, resolution_minutes: 120 });

  const load = useCallback(() => {
    api.get("/sla-overrides").then((r) => setRows(r.data || []));
    api.get("/departments").then((r) => setDepts(r.data || [])).catch(() => {});
    api.get("/customer-clients").then((r) => setClients(r.data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const scopeOptions = form.scope_type === "department" ? depts : clients;
  const scopeName = (o) => {
    const list = o.scope_type === "department" ? depts : clients;
    return list.find((x) => x.id === o.scope_id)?.name || o.scope_label || o.scope_id;
  };

  const add = async () => {
    if (!form.priority_key || !form.scope_id) { toast.error("Pick a priority and a scope target."); return; }
    const label = scopeOptions.find((x) => x.id === form.scope_id)?.name || "";
    try {
      await api.post("/sla-overrides", {
        ...form, scope_label: label,
        first_response_minutes: Number(form.first_response_minutes),
        resolution_minutes: Number(form.resolution_minutes),
      });
      toast.success("Override added"); load();
      setForm({ ...form, scope_id: "" });
    } catch (e) { toast.error(errMsg(e, "Add failed")); }
  };
  const del = async (r) => {
    try { await api.delete(`/sla-overrides/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <h3 className="font-heading text-base flex items-center gap-2 mb-1"><Layers className="w-4 h-4 text-indigo-500" /> Scoped SLA Overrides</h3>
        <p className="text-xs text-slate-500 mb-4">Override a priority's SLA for a specific Department or Client. Precedence: <b>Client → Department → Global</b>.</p>
        {canWrite && (
          <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
            <div className="space-y-1"><Label className="text-[11px]">Priority</Label>
              <Select value={form.priority_key} onValueChange={(v) => setForm({ ...form, priority_key: v })}>
                <SelectTrigger className="h-9 w-32" data-testid="ovr-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>{priorities.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Scope</Label>
              <Select value={form.scope_type} onValueChange={(v) => setForm({ ...form, scope_type: v, scope_id: "" })}>
                <SelectTrigger className="h-9 w-32" data-testid="ovr-scope-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="department">Department</SelectItem><SelectItem value="client">Client</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Target</Label>
              <Select value={form.scope_id} onValueChange={(v) => setForm({ ...form, scope_id: v })}>
                <SelectTrigger className="h-9 w-40" data-testid="ovr-scope-id"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{scopeOptions.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-[11px]">FR (min)</Label>
              <Input type="number" min="1" className="h-9 w-20" value={form.first_response_minutes} onChange={(e) => setForm({ ...form, first_response_minutes: e.target.value })} data-testid="ovr-fr" /></div>
            <div className="space-y-1"><Label className="text-[11px]">Res (min)</Label>
              <Input type="number" min="1" className="h-9 w-20" value={form.resolution_minutes} onChange={(e) => setForm({ ...form, resolution_minutes: e.target.value })} data-testid="ovr-res" /></div>
            <Button size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700" onClick={add} data-testid="add-override-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
        <ul className="space-y-1.5" data-testid="overrides-list">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-white px-3 py-2 text-sm">
              <span><b className="capitalize">{r.priority_key}</b> · {r.scope_type}: {scopeName(r)} → FR {fmtMins(r.first_response_minutes)}, Res {fmtMins(r.resolution_minutes)}</span>
              {canWrite && <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => del(r)} data-testid={`delete-override-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </li>
          ))}
          {rows.length === 0 && <li className="text-sm text-slate-400 py-2">No overrides — all priorities use their global SLA.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function SlaPoliciesManager() {
  const { hasPerm } = useAuth();
  const canWrite = hasPerm("catalogue.manage_sla");
  return (
    <div className="space-y-6" data-testid="sla-policies-manager">
      <PrioritiesSection canWrite={canWrite} />
      <OverridesSection canWrite={canWrite} />
      <ClientsSection canWrite={canWrite} />
    </div>
  );
}
